import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Trophy } from "@phosphor-icons/react";

// Animated winner celebration overlay. Auto-hides after `duration` ms (default 60s),
// leaving the persistent trophy next to the winner in the scoreboard rows.
export default function WinnerCelebration({ team, accent = "green", size = "md", duration = 60000, children }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setVisible(false), duration);
    return () => clearTimeout(t);
  }, [duration]);

  const color = accent === "green" ? "#CCFF00" : "#00D1FF";
  const nameSize =
    size === "lg" ? "text-5xl sm:text-7xl lg:text-8xl" :
    size === "sm" ? "text-2xl sm:text-3xl" :
    "text-4xl sm:text-6xl";
  const trophy = size === "lg" ? 110 : size === "sm" ? 44 : 72;

  if (!visible || !team) return null;

  return (
    <motion.div
      data-testid="winner-celebration"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="absolute inset-0 z-30 flex flex-col items-center justify-center text-center bg-black/75 backdrop-blur-md overflow-hidden"
    >
      {/* radial glow */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.55 }}
        transition={{ duration: 0.6 }}
        className="absolute w-[140%] aspect-square rounded-full blur-3xl pointer-events-none"
        style={{ background: `radial-gradient(circle, ${color}40, transparent 60%)` }}
      />
      {/* sweeping shine with fade in / fade out, starts off-screen left */}
      <motion.div
        initial={{ x: "-250%", opacity: 0 }}
        animate={{ x: ["-250%", "180%"], opacity: [0, 0, 1, 1, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 1, ease: "easeInOut", times: [0, 0.12, 0.3, 0.8, 1] }}
        className="absolute inset-y-0 w-1/3 skew-x-12 pointer-events-none"
        style={{ background: `linear-gradient(90deg, transparent, ${color}26, transparent)` }}
      />

      <motion.div
        initial={{ scale: 0.3, rotate: -18, opacity: 0 }}
        animate={{ scale: 1, rotate: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 13 }}
        className="relative z-10 flex flex-col items-center px-6"
      >
        <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}>
          <Trophy size={trophy} weight="fill" style={{ color, filter: `drop-shadow(0 0 24px ${color})` }} />
        </motion.div>
        <p className="mt-3 font-heading uppercase tracking-[0.35em] text-xs sm:text-base" style={{ color }}>¡Ganador!</p>
        <div className={`mt-1 font-heading font-extrabold uppercase ${nameSize} leading-[0.95] flex flex-col`} style={{ color: "#fff", textShadow: `0 0 30px ${color}` }}>
          <span>{team.p1}</span>
          {team.p2 && <span>{team.p2}</span>}
        </div>
        {children && <div className="mt-6">{children}</div>}
      </motion.div>
    </motion.div>
  );
}
